import React from 'react'
import { Typography } from '@mui/material'
import {
  Page,
  Section,
  Bullets,
  Bullet,
  Info,
  Warning,
  Step,
  Screenshot,
} from '../../components/DocsComponents.jsx'
import DocLink from '../../components/DocLink.jsx'

const XeroExport = () => (
  <Page
    title="Exporting Invoices to Xero"
    intro="AmbuNet is not your accounting system — Xero (or similar) is. The Xero export
    turns any selection of invoices into a CSV file in Xero's sales-invoice import
    format, so invoices raised in AmbuNet reach your accounts without retyping."
  >
    <Section title="Running an export">
      <Step number={1} title="Select the invoices">
        <Typography>
          On <DocLink url="/invoices" text="Invoices" />, tick the invoices to
          export using the checkboxes. The source toggle helps here — filter to
          Events or PTS first if you export the two sides separately.
        </Typography>
      </Step>
      <Step number={2} title="Export">
        <Typography>
          Click <strong>Export N to Xero (CSV)</strong>. A CSV file downloads,
          named with today's date, containing one row per invoice line item.
        </Typography>
      </Step>
      <Step number={3} title="Import into Xero">
        <Typography>
          In Xero, use Business → Invoices → Import and upload the file. Review
          the draft invoices in Xero before approving them.
        </Typography>
      </Step>
      <Screenshot url="finance/xero-export.png">
        Invoices page with several invoices ticked and the Export to Xero
        button active.
      </Screenshot>
    </Section>

    <Section title="What the file contains">
      <Bullets>
        <Bullet>
          One row per invoice line item, in Xero's invoice-import column
          layout: ContactName, EmailAddress, InvoiceNumber, InvoiceDate,
          DueDate, Description, Quantity, UnitAmount, Discount, AccountCode,
          TaxType, and Currency.
        </Bullet>
        <Bullet>
          <strong>Draft invoices are skipped</strong> — only approved invoices
          are exported. Skipped drafts are reported with the download, and if
          everything you selected is still a draft the export is refused.
        </Bullet>
        <Bullet>
          <strong>Credit notes</strong> issued against an exported invoice go
          out as their own documents under their CN- reference, with negative
          amounts — Xero creates matching credit notes from the negative
          import.
        </Bullet>
        <Bullet>
          A flat invoice <strong>discount</strong> becomes a per-line
          percentage in the Discount column, so the total Xero imports matches
          the net amount in AmbuNet.
        </Bullet>
        <Bullet>
          <strong>AccountCode</strong> comes from the Default Nominal Code, and{' '}
          <strong>TaxType</strong> from the per-rate Xero tax-rate names, both
          set under Configuration → Company Settings →{' '}
          <strong>Accounting Export</strong>. Set the tax-rate names exactly as
          they appear in your Xero organisation; sensible UK and Irish defaults
          are used otherwise.
        </Bullet>
        <Bullet>
          The currency follows your organisation's region: GBP for UK tenants,
          EUR for Irish tenants.
        </Bullet>
      </Bullets>
      <Info>
        This is a file export, not a live Xero connection. Each export stamps
        the invoices it included with the export date and a batch id, so the
        invoice list can show what has already been exported — but exporting
        the same invoice twice still produces it in both files, and Xero will
        not stop you importing it twice. Keep a routine (for example, export at
        each month end) so nothing is imported twice or missed.
      </Info>
      <Warning>
        The PTS invoicing screen has its own Xero export covering PTS invoices
        with the same column layout — see{' '}
        <DocLink
          url="/patient-transport/invoicing"
          text="PTS Quoting, Invoicing & Billing"
          docsPage
        />
        . If your PTS team already exports from there, agree which team exports
        what to avoid importing the same invoices into Xero twice.
      </Warning>
    </Section>

    <Section title="Who can do this?">
      <Typography>
        The export requires the <strong>Finance</strong> team role. The
        PTS-side export requires PTS <strong>Co-Ordinator</strong> access.
      </Typography>
    </Section>
  </Page>
)

export default XeroExport
